import { GalleryImage } from '../types/gallery';
import { apiFetch } from '@/lib/api';

export interface GalleryListResult {
	items: GalleryImage[];
	total: number;
	page: number;
	pageSize: number;
	hasMore: boolean;
}

export class GalleryService {
	private orgHeaders(): Record<string, string> {
		try {
			const orgId = localStorage.getItem('ima_active_org_id');
			return orgId ? { 'X-Organization-Id': orgId } : {};
		} catch {
			return {};
		}
	}

	private mapItem(f: any): GalleryImage {
		return {
			id: f.id,
			filename: f.filename || f.originalName || f.title || f.stored_filename,
			originalName: f.originalName || f.original_filename || f.title || '',
			url: f.url,
			thumbnail: f.thumbnail || f.thumbnail_url || f.url,
			title: f.title,
			metadata: {
				uploadedAt: new Date(f.metadata?.uploadedAt || f.created_at || Date.now()),
				size: f.metadata?.size ?? f.size ?? 0,
				dimensions: f.metadata?.dimensions ?? { width: f.width ?? 0, height: f.height ?? 0 },
				tags: f.metadata?.tags ?? f.tags ?? [],
				category: 'gallery',
			},
			description: f.description || null,
			aiAnalyzedAt: f.aiAnalyzedAt ? new Date(f.aiAnalyzedAt) : null,
		};
	}

	async listGalleryImages(q?: string): Promise<GalleryImage[]> {
		const result = await this.listGalleryImagesPaginated({ q, pageSize: 50 });
		return result.items;
	}

	async listGalleryImagesPaginated(opts: {
		q?: string;
		page?: number;
		pageSize?: number;
	} = {}): Promise<GalleryListResult> {
		const { q, page = 1, pageSize = 50 } = opts;
		try {
			const params = new URLSearchParams();
			if (q) params.set('q', q);
			params.set('page', String(page));
			params.set('pageSize', String(pageSize));
			const response = await apiFetch(`/api/gallery?${params.toString()}`, {
				headers: this.orgHeaders()
			});
			if (!response.ok) {
				return { items: [], total: 0, page, pageSize, hasMore: false };
			}
			const payload: { items: Array<any>; total: number } = await response.json();
			const files = payload.items || [];
			const items = files.map((f: any) => this.mapItem(f));
			const total = payload.total || 0;
			const hasMore = page * pageSize < total;
			return { items, total, page, pageSize, hasMore };
		} catch (error) {
			console.error('Failed to list gallery images:', error);
			return { items: [], total: 0, page, pageSize, hasMore: false };
		}
	}
} 